<template>
  <DialogHeader>Share this file</DialogHeader>
  <DialogContent>
    <div class="mb-2">Share a direct link to this copyright</div>
    <div
      class="w-full h-12 px-[18px] border border-gray-800/25 rounded-sm flex justify-between items-center font-secondary select-none"
    >
      <div class="text-ellipsis overflow-hidden whitespace-nowrap">
        {{ url }}
      </div>
      <div class="relative ml-4">
        <Tooltip manual>
          <TooltipTrigger>
            <div
              class="flex items-center font-secondary whitespace-nowrap cursor-pointer font-medium text-green-700"
              @click="handleCopyClick"
            >
              <Icon :size="16" name="Link" class="stroke-green-700 mr-1" />
              Copy link
            </div>
          </TooltipTrigger>
          <TooltipContent v-if="copied">Copied!</TooltipContent>
        </Tooltip>
      </div>
    </div>

    <template v-if="showInvite">
      <div class="w-full h-[1px] bg-gray-800/20 mt-[26px] mb-5"></div>

      <div
        class="flex items-center font-semibold font-primary text-[14px] leading-[18px] mb-1"
      >
        <Icon :size="18" name="Send" class="mr-2.5 stroke-gray-800" />
        <div>Invite to view and download this file</div>
      </div>
      <div>Control who can view this file and who has download access</div>

      <Link
        v-if="file.viewers?.totalCount"
        class="mt-1"
        @click="handleAccessClick"
      >
        {{ viewerCount }}
      </Link>
      <UiButton class="mt-3" @click="handleInviteClick"> Invite user </UiButton>
    </template>
  </DialogContent>
</template>

<script setup lang="ts">
import DialogHeader from '@/shared/dialogs/DialogHeader.vue'
import DialogContent from '@/shared/dialogs/DialogContent.vue'
import { useDialog } from '@/shared/dialogs/useDialog'
import { Dialogs } from '@/shared/dialogs/types'
import { computed, ref } from 'vue'
import { Tooltip, TooltipTrigger, TooltipContent } from '@dacr/ui/next'
import { Icon, UiButton } from '@dacr/ui'
import { FileRole } from '@/shared/graphql/router/graphql'
import type { File } from '@/shared/graphql/router/graphql'
import Link from '@/shared/components/Link.vue'

const { close, open } = useDialog()

const props = defineProps<{
  file: File
}>()

const file = ref({ ...props.file })
const copied = ref(false)
let interval: ReturnType<typeof setTimeout>

const url = computed(
  () => `${window.location.origin}/file/${file.value.copyright.id}`
)

const showInvite = computed(
  () =>
    file.value.role === FileRole.Owner || file.value.role === FileRole.CoOwner
)

const viewerCount = computed(() => {
  const count = file.value.viewers?.totalCount
  if (count === 1) return '1 person'
  return `${count} people`
})

const handleCopyClick = async () => {
  clearInterval(interval)
  await navigator.clipboard.writeText(url.value)
  copied.value = true

  interval = setTimeout(() => {
    copied.value = false
  }, 1500)
}

const handleAccessClick = () => {
  open(Dialogs.FileAccess, {
    props: { file: file.value },
    stack: true,
    events: {
      success(data: File) {
        file.value = data
      },
    },
  })
}

const handleInviteClick = () => {
  open(Dialogs.FileAccess, {
    props: { file: file.value },
    stack: true,
    events: {
      success(data: File) {
        file.value = data
      },
    },
  })
}
</script>
